import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CatalogEntry } from '../../../api/admin';
import { getIntegrationCatalog } from '../../../api/admin';
import {
  RunCounts, RunFilter, RunStep, RunSummary, RunsResponse, listRunsApi, retryDeliveryApi,
} from '../../../api/forms';
import { Modal, TableCard } from '../../../components/fs';
import { useToast } from '../../../components/Toast';
import { DESKTOP, useMediaQuery } from '../../../shared/media';
import { useFormWorkspace } from '../FormWorkspace';
import { relativeDay } from '../FormsList';
import { usePageHeight } from './usePageHeight';
import { PHONE } from '../../../shared/media';
import './workflow.css';

/**
 * Automate › Runs (R6, screen 15): one row per submission — which way it
 * went through the workflow and what each step did — with the tab counts,
 * and the selected run as a timeline beside the table.
 *
 * The rows come from `GET /admin/forms/:id/runs`, which replays each
 * submission against the workflow AS IT IS DRAWN NOW (the server records no
 * run of its own — see `services/delivery/runs.ts`), so the page says so.
 *
 * Retry is the existing per-delivery retry, the one the response drawer's
 * Deliveries and Workflow tabs already call: a second way in, never a second
 * code path. Reconnect opens the step in the workflow's step dialog through
 * the `?node=` link the Document tab already uses.
 */

const FILTERS: { key: RunFilter; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'failed', label: 'Failed' }, { key: 'waiting', label: 'Waiting' }, { key: 'done', label: 'Done' },
];
const PAGE = 25;

/** The mark a step gets on the timeline and the colour of its bar. */
function tone(step: RunStep): string {
  switch (step.state) {
    case 'done': return 'done';
    case 'failed': return 'failed';
    case 'waiting': case 'pending': return 'waiting';
    default: return step.state;
  }
}
const MARK: Record<string, string> = { done: '✓', failed: '!', waiting: '◷', pending: '◷', skipped: '–', off: '–', unrecorded: '·' };

/** "Today 9:03 AM", "Yesterday 4:10 PM", or the date. */
export function startedText(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const day = relativeDay(iso, now);
  if (day === 'today' || day === 'yesterday') return `${day[0].toUpperCase()}${day.slice(1)} ${time}`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ` ${time}`;
}

export default function RunsPage() {
  const { form } = useFormWorkspace();
  const toast = useToast();
  const desktop = useMediaQuery(DESKTOP);
  const phone = useMediaQuery(PHONE);
  // Full-bleed (R6 follow-up): the table across the page, the timeline a
  // locked column on the right, the pair filling the screen under the bar.
  const pageRef = useRef<HTMLDivElement>(null);
  usePageHeight(pageRef, !phone);
  const [filter, setFilter] = useState<RunFilter>('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<RunsResponse | null>(null);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  /** below 1100 the timeline is a modal (a sheet on a phone), opened by a row */
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [retrying, setRetrying] = useState<number | null>(null);

  const load = useCallback(() => listRunsApi(form.id, { status: filter, page, limit: PAGE })
    .then((r) => { setData(r); setError(''); })
    .catch((e) => setError(e.message)), [form.id, filter, page]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    getIntegrationCatalog().then((r) => setCatalog([...r.builtIn, ...r.integrations])).catch(() => undefined);
  }, []);

  const runs = data?.runs || [];
  const counts: RunCounts = data?.counts || { all: 0, failed: 0, waiting: 0, done: 0 };
  // The design opens on the run that needs somebody: the first failed one,
  // else the first. A row the person picked stays picked across a reload.
  const selected = useMemo(() => {
    if (selectedId !== null) {
      const hit = runs.find((r) => r.id === selectedId);
      if (hit) return hit;
    }
    return runs.find((r) => r.status === 'failed') || runs[0] || null;
  }, [runs, selectedId]);

  const serviceName = (step: RunStep) => catalog.find((c) => c.type === step.connectionType)?.title || step.title;

  async function retry(step: RunStep) {
    if (!step.deliveryId) return;
    setRetrying(step.deliveryId);
    try {
      const r = await retryDeliveryApi(step.deliveryId);
      toast(r.success ? `${step.title} ran again — ${r.delivery?.status === 'skipped' ? 'skipped' : 'sent'}.` : `${step.title} failed again: ${r.delivery?.last_error || 'no detail'}`);
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setRetrying(null); }
  }

  const pick = (run: RunSummary) => { setSelectedId(run.id); if (!desktop) setOpen(true); };
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / PAGE));

  const timeline = selected ? (
    <>
      <div className="wf-run-head">
        <b>Run for #{selected.submissionId.replace(/^SUB-/, '')}{selected.label !== selected.submissionId ? ` ${selected.label}` : ''}</b>
        <span className="sub">
          {startedText(selected.startedAt)} · {selected.path === 'Straight through' ? 'straight through' : `${selected.path} path`}
          {' · '}<Link to={`../submissions/${selected.id}`}>Open the response</Link>
        </span>
      </div>
      <div className="wf-run-steps">
        {selected.steps.map((s, i) => (
          <div key={`${s.nodeId}-${i}`} className={`wf-run-step${s.stray ? ' stray' : ''}`}>
            <span className={`mark ${tone(s)}`} aria-hidden="true">{MARK[s.state] || '·'}</span>
            <span className="text">
              <b>{s.title}</b>
              <span className={`detail${s.state === 'failed' ? ' failed' : ''}`}>{s.detail}</span>
            </span>
          </div>
        ))}
        {selected.steps.filter((s) => s.retryable && s.deliveryId).map((s) => (
          <div key={`retry-${s.deliveryId}`}>
            <div className="wf-run-actions">
              <button
                type="button" className="fs-btn primary sm" disabled={retrying === s.deliveryId}
                onClick={() => void retry(s)}
              >
                {retrying === s.deliveryId ? 'Running…' : `Retry ${serviceName(s)} step`}
              </button>
              {!s.stray && (
                <Link className="fs-btn neutral sm" to={`../workflow?node=${encodeURIComponent(s.nodeId)}`}>
                  Reconnect {serviceName(s)}
                </Link>
              )}
            </div>
            <div className="wf-run-actions-note">
              A retry re-runs this step only.{s.connectionType !== 'email' ? ' Nobody gets a second email.' : ''}
            </div>
          </div>
        ))}
      </div>
    </>
  ) : (
    <div className="wf-runs-empty-side">Pick a run to see what each step did.</div>
  );

  return (
    <div className="wf-runs" ref={pageRef}>
      <div className="wf-runs-main">
        <div className="wf-runs-head">
          <div className="fs-seg" role="group" aria-label="Which runs to show">
            {FILTERS.map((f) => (
              <button
                key={f.key} type="button" className={filter === f.key ? 'on' : ''} aria-pressed={filter === f.key}
                onClick={() => { setFilter(f.key); setPage(1); }}
              >
                {f.label} <span className="count">{counts[f.key]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="wf-runs-note">
          Each run is the path through the <b>current</b> workflow, replayed from the response's answers and
          matched to what each step recorded. A step drawn since a response arrived shows as not recorded.
        </div>
        {error && <div className="ad-err">{error}</div>}
        <TableCard
          className="wf-runs-card"
          footer={data && data.total > PAGE ? (
            <div className="wf-runs-foot">
              <span>Page {page} of {totalPages}</span>
              <span className="spacer" />
              <button type="button" className="fs-btn neutral sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Newer</button>
              <button type="button" className="fs-btn neutral sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Older ›</button>
            </div>
          ) : undefined}
        >
          <table className="fs-table wf-runs-table">
            <thead>
              <tr><th>#</th><th>Response</th><th>Path</th><th>Steps</th><th>Status</th><th>Started</th></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr
                  key={r.id}
                  className={`${selected?.id === r.id ? 'on' : ''} ${r.status}`.trim()}
                  onClick={() => pick(r)}
                  aria-selected={selected?.id === r.id}
                >
                  <td className="wf-runs-num">#{r.submissionId.replace(/^SUB-/, '')}</td>
                  <td>
                    <Link className="wf-runs-who" to={`../submissions/${r.id}`} onClick={(e) => e.stopPropagation()}>{r.label}</Link>
                  </td>
                  <td className="wf-runs-path">{r.path}</td>
                  <td>
                    <span className="wf-bars" title={r.steps.map((s) => `${s.title}: ${s.detail}`).join('\n')}>
                      {r.steps.map((s, i) => <span key={i} className={`wf-bar ${tone(s)}`} />)}
                    </span>
                  </td>
                  <td><span className={`wf-runs-status ${r.status}`}>{r.statusText}</span></td>
                  <td className="wf-runs-when" title={new Date(r.startedAt).toLocaleString()}>{startedText(r.startedAt)}</td>
                </tr>
              ))}
              {data && runs.length === 0 && (
                <tr><td colSpan={6} className="wf-runs-empty">
                  {filter === 'all' ? 'No responses yet — a run appears here for each one.' : `No ${filter} runs.`}
                </td></tr>
              )}
            </tbody>
          </table>
        </TableCard>
      </div>

      {desktop && <aside className="wf-runs-side" aria-label="The selected run">{timeline}</aside>}
      {!desktop && open && selected && (
        <Modal title={`Run for #${selected.submissionId.replace(/^SUB-/, '')}`} onClose={() => setOpen(false)}>
          <div className="wf-runs-modal">{timeline}</div>
        </Modal>
      )}
    </div>
  );
}
